import React, {
  useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

import useAtsRequisitionsApi, { REQUISITION_STATUSES } from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import A11yDialog from '../../components/ui/A11yDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import RequisitionDetailDrawer, { requisitionPositionLabel } from './RequisitionDetailDrawer';
import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';
import Button from '../../components/ui/Button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  ownerIdsOf, visibleTransitions, canEditRow, canDeleteRow, PENDING_APPROVAL_MODE,
  POSITION_MODES, offersCloseUnfilled, offersApprovedHeadcount, openSeatCount,
} from './requisitionActions';

// Derin bağlantı ön doldurma parametreleri (Ekibim / org şeması → kadro formu).
// `mode` anahtarı Onaylarım kipiyle (pendingApproval) PAYLAŞILIR — yalnız
// existing | new değerleri bu akışa aittir.
const PREFILL_KEYS = ['new', 'positionId', 'parentPositionId'];
// Kimlikler URL'den gelir ve API yoluna girer: yalnız kimlik biçimli değer kabul edilir.
const SAFE_ID = /^[A-Za-z0-9-]{1,64}$/;

export function parsePrefillQuery(search) {
  const q = new URLSearchParams(search || '');
  if (q.get('new') !== '1') return null;
  const mode = q.get('mode');
  if (!POSITION_MODES.includes(mode)) return null;
  const id = (k) => { const v = q.get(k); return v && SAFE_ID.test(v) ? v : null; };
  if (mode === 'existing') return { mode, positionId: id('positionId') };
  return { mode, parentPositionId: id('parentPositionId') };
}

const HeadcountHint = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Container = styled.div``;

// Reason-capture dialog (used for transitions that require a justification, e.g.
// reject). Mirrors the status dialog pattern in AtsApplicationsPage so the look
// and behaviour stay consistent across ATS pages.
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;

  /* FE-08 — telefonda 24px x 2 = 48px, 390px ekranin %12'si. */
  @media (max-width: 600px) {
    padding: 12px;
  }
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 480px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  /* FE-08 — max-height YOKTU: perde "position: fixed; inset: 0" +
     "align-items: center" olduğundan, karttan uzun bir içerik ekranın ALTINDAN
     VE ÜSTÜNDEN taşıyordu ve hiçbir yolla kaydırılamıyordu — "Onayla"
     düğmesine telefonda hiç ulaşılamıyordu. "100%" perdenin iç kutusuna göre
     çözülür, yani perdenin iç boşluğu 24px'ten 12px'e inince kart otomatik
     olarak büyür (sabit 90vh bunu yapamaz).
     Bu, kabuğun "tek kaydırma" kuralını ÇİĞNEMEZ: diyalog ayrı bir katmandır,
     altındaki Main bu sırada zaten kilitlidir. */
  max-height: 100%;
  overflow-y: auto;
`;

const DialogTitle = styled.h3`
  margin: 0 0 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  margin-bottom: 18px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

// Satırın birincil kararı ⋮ menüsünde SAKLANMAZ — teklifler sayfası bu dersi
// öğrenmişti (AtsOffersPage RowBtn), kadro talepleri öğrenmemişti (FE-13).
const RowActions = styled.div` display: flex; align-items: center; justify-content: flex-end; gap: 6px; `;
const RowBtn = styled.button`
  border: none; border-radius: 4px; padding: 5px 12px; font-size: 12px; font-weight: 600;
  cursor: pointer; white-space: nowrap; color: #fff; font-family: inherit;
  background: ${({ $tone, theme }) =>
    $tone === 'approve' ? '#10B981' : $tone === 'reject' ? '#E53935' : theme.colors.primary.main};
  &:hover:not(:disabled) { opacity: 0.88; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

// Uygulanan süzgecin AFİŞİ. Kaynağı `filterList` DEĞİL (DataTable o prop'u
// yutuyor — mui-datatables@4.3.0 okumuyor), doğrudan URL'den türeyen
// `pendingOnly`: kullanıcı neyin süzüldüğünü görmeden "6 talep görünmüyor"
// diye düşünmemeli.
const FilterChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px 5px 12px;
  margin-right: 8px;
  border-radius: 14px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.dark};
  background: ${({ theme }) => `${theme.colors.primary.main}1A`};
`;

const ChipClear = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
`;

// Durum makinesi + rol-bazlı aksiyon görünürlüğü requisitionActions.js'te
// (backend AtsRequisitionDataAccess.ChangeStatus'un FE karşılığı).

export default function AtsRequisitionsPage() {
  const { t } = useTranslation();
  const { user, canWrite } = useAuth();
  const writable = canWrite ? canWrite('AT') : false;
  const history = useHistory();
  const location = useLocation();

  // Onaylarım kartının derin bağlantısı. Süzgeç SUNUCUDA uygulanır
  // (pendingForMe): "benim kararımı bekleyenler" yüklemi kartın saydığı
  // yüklemin AYNISIDIR, yani kart 6 deyip liste 6 satır gösterir.
  const pendingOnly = useMemo(
    () => new URLSearchParams(location?.search || '').get('mode') === PENDING_APPROVAL_MODE,
    [location],
  );

  // İşe Alım Panosu'nun "Açık Kadrolar" KPI'ı buraya derin bağlanır:
  // ?status=approved,posting_opened. Sunucu anahtarı `status[in]` CSV'dir
  // (AtsRequisitionFilterRequest.StatusIn) — tablo süzgecinin ÜRETTİĞİ anahtarın
  // AYNISI. Tanınmayan değer ATILIR: URL'den gelen serbest metin sunucuya sızmaz.
  const statusScope = useMemo(() => {
    const raw = new URLSearchParams(location?.search || '').get('status') || '';
    const list = raw.split(',').map((x) => x.trim()).filter((x) => REQUISITION_STATUSES.includes(x));
    return list.length ? list.join(',') : null;
  }, [location]);

  const statusScopeLabels = useMemo(() => (statusScope
    ? statusScope.split(',').map((k) => t(`ats.requisition.status.${k}`, { defaultValue: k })).join(', ')
    : ''), [statusScope, t]);

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  // parseTableParams her tablo değişiminde parametreleri SIFIRDAN kurar; kip
  // bayrağı bu yüzden state'e karıştırılmaz, türetilmiş katman olarak eklenir.
  // Durum kapsamı VARSAYILANDIR: kullanıcı tablodan kendi durum süzgecini
  // seçerse parseTableParams'in ürettiği `status[in]` kazanır, URL kapsamı
  // onu EZMEZ (aksi hâlde derin bağlantıdan sonra durum süzgeci ölürdü).
  const effectiveParams = useMemo(
    () => ({
      ...(statusScope ? { 'status[in]': statusScope } : {}),
      ...queryParams,
      ...(pendingOnly ? { pendingForMe: true } : {}),
    }),
    [queryParams, pendingOnly, statusScope],
  );

  const clearPendingFilter = useCallback(() => {
    history.replace(location?.pathname || '/ats/requisitions');
  }, [history, location]);

  // Yalnız durum kapsamını düşürür — `mode` kipi varsa KORUNUR (iki bağımsız çip).
  const clearStatusFilter = useCallback(() => {
    const q = new URLSearchParams(location?.search || '');
    q.delete('status');
    const search = q.toString();
    history.replace({ pathname: location?.pathname || '/ats/requisitions', search: search ? `?${search}` : '' });
  }, [history, location]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  // Yeni talebin ön doldurulmuş başlangıç değerleri (derin bağlantı). Düz "Yeni"de null
  // — alanların `defaultValue`'su (mod: existing) çekmecede tohumlanır.
  const [formInitial, setFormInitial] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const [statusDialog, setStatusDialog] = useState({ isOpen: false, id: null, row: null, title: '', action: '', actionLabel: '', requiresReason: false });
  const [statusReason, setStatusReason] = useState('');
  // İptal / erken kapanış: talebin açtığı boş pozisyonları da kapat (varsayılan işaretli).
  const [closeUnfilled, setCloseUnfilled] = useState(true);
  // Onay (yalnız `new` mod): onaycı açılacak pozisyon sayısını 1..talep aralığında düşürebilir.
  const [approvedHeadcount, setApprovedHeadcount] = useState('');
  const [statusBusy, setStatusBusy] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, id: null, title: '' });
  const [deleting, setDeleting] = useState(false);

  const {
    fetching, data, pagination, columns: baseColumns, objectSchema,
    create, update, changeStatus, remove, noteHiringManager, buildPrefill,
  } = useAtsRequisitionsApi(effectiveParams, { writable });

  // ?new=1&mode=existing&positionId=… | ?new=1&mode=new&parentPositionId=… → formu
  // ön doldurulmuş aç. Aynı arama dizgesi bir kez işlenir (yeniden çizim formu
  // yeniden açmasın); kapanışta sorgu temizlenir (bkz. closeForm).
  const prefillQuery = useMemo(() => parsePrefillQuery(location?.search), [location]);
  const handledPrefillRef = useRef(null);
  useEffect(() => {
    if (!prefillQuery) { handledPrefillRef.current = null; return; }
    const key = location?.search || '';
    if (handledPrefillRef.current === key) return;
    handledPrefillRef.current = key;
    Promise.resolve(buildPrefill ? buildPrefill(prefillQuery) : null)
      .catch(() => null)
      .then((init) => {
        const values = init || { positionMode: prefillQuery.mode };
        noteHiringManager(values.hiringManagerUserId ?? null);
        setSelectedRow(null);
        setFormInitial(values);
        setDrawerOpen(true);
      });
  }, [prefillQuery, location, buildPrefill, noteHiringManager]);

  // Form kapanışı: ön doldurma sorgusu URL'den düşer — `replace` ile, yani tarayıcının
  // geri tuşu formu YENİDEN AÇMAZ (geçmişte ?new=1 girdisi kalmaz). Diğer anahtarlar
  // (status kapsamı vb.) korunur.
  const closeForm = useCallback(() => {
    setDrawerOpen(false);
    setFormInitial(null);
    if (!parsePrefillQuery(location?.search)) return;
    const q = new URLSearchParams(location?.search || '');
    PREFILL_KEYS.forEach((k) => q.delete(k));
    if (POSITION_MODES.includes(q.get('mode'))) q.delete('mode');
    const search = q.toString();
    history.replace({ pathname: location?.pathname || '/ats/requisitions', search: search ? `?${search}` : '' });
  }, [history, location]);

  // HR BP seçicisinin HM dışlaması için seçili HM hook'a bildirilir: yeni formda yok,
  // düzenlemede satırın HM'si (initialValues drawer'a doğrudan girer; alan kancası tetiklenmez).
  const handleNewButtonClick = useCallback(() => {
    noteHiringManager(null);
    setSelectedRow(null);
    setFormInitial(null);
    setDrawerOpen(true);
  }, [noteHiringManager]);

  // Only draft / pending_approval rows are still safe to edit; afterwards the
  // status state machine takes over and editing the salary band etc. would
  // corrupt the audit trail.
  const handleEdit = useCallback((row) => {
    noteHiringManager(row?.hiringManagerUserId ?? null);
    setSelectedRow(row);
    setDrawerOpen(true);
  }, [noteHiringManager]);

  const handleSave = useCallback(async (formData) => {
    const res = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (res) {
      closeForm();
      // Onay bekleyen talep düzenlenince backend taslağa döndürür (`returnedToDraft`) — kullanıcı
      // yeniden onaya göndermesi gerektiğini bilmeli. Liste hook'un update'inde yenilenir.
      if (selectedRow && res.returnedToDraft === true) toast.info(t('ats.requisition.returnedToDraft'));
    }
    return res;
  }, [selectedRow, create, update, t, closeForm]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const closeStatusDialog = useCallback(() => {
    setStatusDialog({ isOpen: false, id: null, row: null, title: '', action: '', actionLabel: '', requiresReason: false });
    setStatusReason('');
    setCloseUnfilled(true);
    setApprovedHeadcount('');
  }, []);

  // Diyaloğa özgü ek girdiler — satırın moduna ve açık koltuğuna göre.
  const showCloseUnfilled = statusDialog.isOpen && offersCloseUnfilled(statusDialog.row, statusDialog.action);
  const showApprovedHeadcount = statusDialog.isOpen && offersApprovedHeadcount(statusDialog.row, statusDialog.action);
  const requestedMax = Number(statusDialog.row?.requestedHeadcount) || 1;
  const approvedHeadcountValid = !showApprovedHeadcount || (
    /^\d+$/.test(String(approvedHeadcount).trim())
    && Number(approvedHeadcount) >= 1
    && Number(approvedHeadcount) <= requestedMax
  );

  const handleStatusConfirm = useCallback(async () => {
    if (!statusDialog.id) return;
    // Reject (and any future reason-gated transition) must carry a justification;
    // the backend rejects an empty reason with ats.requisition.rejectReasonRequired.
    if (statusDialog.requiresReason && !statusReason.trim()) return;
    if (!approvedHeadcountValid) return;
    const extra = {};
    // Boş pozisyon kapatma kararı YALNIZ tik kutusu gösterildiğinde gönderilir
    // (existing / legacy talepte backend'in varsayılanı zaten hiçbir şeye dokunmaz).
    if (showCloseUnfilled) extra.closeUnfilledPositions = closeUnfilled;
    if (showApprovedHeadcount) extra.approvedHeadcount = Number(approvedHeadcount);
    setStatusBusy(true);
    const ok = Object.keys(extra).length > 0
      ? await changeStatus(statusDialog.id, statusDialog.action, statusReason.trim() || undefined, extra)
      : await changeStatus(statusDialog.id, statusDialog.action, statusReason.trim() || undefined);
    // FE-31 — diyalog ESKİDEN KOŞULSUZ kapanıyordu: sunucu geçişi reddettiğinde
    // (403, geçersiz geçiş, iş kuralı) yazılan red gerekçesi de siliniyordu.
    // Artık yalnız BAŞARIDA kapanır; hata interceptor'ın toast'ıyla bildirilir.
    if (ok) closeStatusDialog();
    setStatusBusy(false);
  }, [
    statusDialog, statusReason, changeStatus, closeStatusDialog,
    showCloseUnfilled, closeUnfilled, showApprovedHeadcount, approvedHeadcount, approvedHeadcountValid,
  ]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialog.id) return;
    setDeleting(true);
    await remove(deleteDialog.id);
    setDeleteDialog({ isOpen: false, id: null, title: '' });
    setDeleting(false);
  }, [deleteDialog, remove]);

  // Kimlik-farkındalıklı aksiyonlar: AT.Write tümünü görür; AT.Read'li kullanıcı
  // yalnız kendi rolüne düşenleri (HM: gönder/iptal/düzenle, HR BP: onay/red/iptal).
  // Backend ChangeStatus/Update aynı kuralları otoriter uygular — FE yalnız yansıtır.
  const authScope = useMemo(
    () => ({ writable, ownerIds: ownerIdsOf(user) }),
    [writable, user],
  );

  // Satır etiketi (onay diyaloglarında gösterilir): "[KOD] Unvan".
  const rowLabelOf = useCallback((row) => requisitionPositionLabel(row), []);

  // TEK KAYNAK: ⋮ menüsü, satır-içi düğmeler ve detay çekmecesinin ayağı AYNI
  // diyalogları açar — üç yerde üç ayrı akış olursa biri sessizce eskir.
  const openStatusDialog = useCallback((row, tr) => {
    setStatusReason('');
    setCloseUnfilled(true);
    setApprovedHeadcount(offersApprovedHeadcount(row, tr.action) ? String(row.requestedHeadcount ?? 1) : '');
    setStatusDialog({
      isOpen: true,
      id: row.id,
      row,
      title: rowLabelOf(row),
      action: tr.action,
      actionLabel: t(tr.labelKey),
      requiresReason: !!tr.requiresReason,
    });
  }, [t, rowLabelOf]);

  // Onaycının birincil kararı: onayla / reddet. Görünürlük backend
  // ChangeStatus'un yetki matrisiyle aynı (requisitionActions tek kaynak).
  const decisionTransitions = useCallback(
    (row) => visibleTransitions(row, authScope).filter((tr) => tr.action === 'approve' || tr.action === 'reject'),
    [authScope],
  );

  const buildInlineButtons = useCallback((row) => {
    const decisions = decisionTransitions(row);
    if (decisions.length === 0) return null;
    return decisions.map((tr) => (
      <RowBtn
        key={tr.action}
        $tone={tr.action === 'approve' ? 'approve' : 'reject'}
        onClick={() => openStatusDialog(row, tr)}
      >
        {t(tr.labelKey)}
      </RowBtn>
    ));
  }, [decisionTransitions, openStatusDialog, t]);

  const buildActions = useCallback((row) => {
    if (!row) return [];
    const actions = [];

    // FE-17 — gerekçeyi, red nedenini ve zaman çizelgesini okumanın tek yolu
    // artık "Düzenle" değil (o, onay bekleyen talebi taslağa geri döndürüyordu).
    actions.push({
      type: 'details',
      label: t('ats.requisition.viewDetail'),
      onClick: () => setDetailRow(row),
    });

    if (canEditRow(row, authScope)) {
      actions.push({
        type: 'edit',
        label: t('ats.requisition.action.edit'),
        onClick: () => handleEdit(row),
      });
    }

    const rowLabel = rowLabelOf(row);

    // "İlana Çevir" — açık talepte (approved: henüz ilanı yok; posting_opened: ilanı var,
    // ikinci ilan açılabilir — backend IsRequisitionOpen aynı küme). Routes to the job
    // postings page with the requisition pre-selected (drawer auto-opens).
    // İlan oluşturma AT.Write gerektirir — kimlik-kapsamlı kullanıcıya gösterilmez.
    if (writable && (row.status === 'approved' || row.status === 'posting_opened')) {
      actions.push({
        type: 'convert_to_posting',
        label: t('ats.requisition.action.convertToPosting'),
        onClick: () => history.push(`/ats/job-postings?createFromRequisition=${row.id}`),
      });
    }

    visibleTransitions(row, authScope).forEach((tr) => {
      actions.push({
        type: tr.action,
        label: t(tr.labelKey),
        onClick: () => openStatusDialog(row, tr),
      });
    });
    if (canDeleteRow(authScope)) {
      if (actions.length > 0) actions.push({ type: 'divider' });
      actions.push({
        type: 'delete',
        label: t('common.delete'),
        onClick: () => setDeleteDialog({ isOpen: true, id: row.id, title: rowLabel }),
      });
    }
    return actions;
  }, [t, writable, authScope, handleEdit, history, rowLabelOf, openStatusDialog]);

  const columns = useMemo(() => {
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const inline = buildInlineButtons(row);
            const actions = buildActions(row);
            if (!inline && actions.length === 0) return null;
            // Aksiyon hücresindeki tıklama satır tıklamasına YÜKSELMEZ —
            // aksi halde ⋮ ya da "Onayla" her basışta detay çekmecesi de açılırdı.
            return (
              <RowActions onClick={(e) => e.stopPropagation()}>
                {inline}
                {actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null}
              </RowActions>
            );
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, buildInlineButtons]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={closeForm}
        title={selectedRow ? t('ats.requisition.edit') : t('ats.requisition.new')}
        schema={objectSchema}
        initialValues={selectedRow || formInitial}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={statusDialog.isOpen && !statusDialog.requiresReason}
        title={statusDialog.actionLabel}
        message={
          t('ats.requisition.statusChangeConfirm', { title: statusDialog.title, action: statusDialog.actionLabel })
          + (statusDialog.action === 'cancel' ? ` ${t('ats.requisition.cancelCascadeWarning')}` : '')
          + (statusDialog.action === 'fill' ? ` ${t('ats.requisition.fillCascadeWarning')}` : '')
        }
        onConfirm={handleStatusConfirm}
        onCancel={closeStatusDialog}
        loading={statusBusy}
        confirmDisabled={!approvedHeadcountValid}
        type="primary"
      >
        {showCloseUnfilled ? (
          <CheckboxInput
            data-role="ats-requisition-close-unfilled"
            label={t('ats.requisition.closeUnfilledPositions', { count: openSeatCount(statusDialog.row) })}
            checked={closeUnfilled}
            onChange={setCloseUnfilled}
          />
        ) : null}
        {showApprovedHeadcount ? (
          <div data-role="ats-requisition-approved-headcount">
            <TextInput
              label={t('ats.requisition.approvedHeadcount')}
              type="number"
              min={1}
              max={requestedMax}
              step={1}
              value={approvedHeadcount}
              onChange={(e) => setApprovedHeadcount(e.target.value)}
              error={approvedHeadcountValid ? undefined : t('ats.requisition.approvedHeadcountRange', { max: requestedMax })}
            />
            <HeadcountHint>{t('ats.requisition.approvedHeadcountHint', { max: requestedMax })}</HeadcountHint>
          </div>
        ) : null}
      </ConfirmDialog>

      {/* Reason-gated transitions (reject) — collect a mandatory justification. */}
      <A11yDialog
        open={statusDialog.isOpen && statusDialog.requiresReason}
        onClose={closeStatusDialog}
        dirty={!!statusReason.trim()}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
            <DialogTitle id={titleId}>{statusDialog.actionLabel} — {statusDialog.title}</DialogTitle>
            <FieldGroup>
              <TextInput
                label={t('ats.requisition.rejectReason')}
                type="textarea"
                rows={3}
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                required
              />
            </FieldGroup>
            <Actions>
              <Button variant="outline" onClick={closeStatusDialog}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleStatusConfirm}
                disabled={statusBusy || !statusReason.trim()}
                loading={statusBusy}
              >
                {t('common.confirm')}
              </Button>
            </Actions>
          </>
        )}
      </A11yDialog>

      <RequisitionDetailDrawer
        open={!!detailRow}
        row={detailRow}
        onClose={() => setDetailRow(null)}
        costAccess={authScope}
        actions={detailRow ? decisionTransitions(detailRow).map((tr) => ({
          action: tr.action,
          label: t(tr.labelKey),
          variant: tr.action === 'reject' ? 'danger' : 'primary',
          onClick: () => { const row = detailRow; setDetailRow(null); openStatusDialog(row, tr); },
        })) : []}
      />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={t('common.delete')}
        message={t('ats.requisition.deleteConfirm', { title: deleteDialog.title })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, id: null, title: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('ats.requisition.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          // Yeni talep: AT.Read yeterli (backend AT.Read Create'i sahiplik-yakalamayla
          // destekler — yönetici kendi talebini açar; kılavuz + modül dokümanıyla uyumlu).
          canCreateNewRecord
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={(row) => setDetailRow(row)}
          toolbarActions={(pendingOnly || statusScope) ? (
            <>
              {pendingOnly && (
                <FilterChip>
                  {t('ats.requisition.pendingForMeBanner')}
                  <ChipClear type="button" onClick={clearPendingFilter} title={t('ats.requisition.clearPendingFilter')}
                    aria-label={t('ats.requisition.clearPendingFilter')}>
                    <X size={13} />
                  </ChipClear>
                </FilterChip>
              )}
              {statusScope && (
                <FilterChip data-role="ats-requisition-status-chip">
                  {t('ats.requisition.statusFilterBanner', { statuses: statusScopeLabels })}
                  <ChipClear type="button" onClick={clearStatusFilter} title={t('ats.requisition.clearStatusFilter')}
                    aria-label={t('ats.requisition.clearStatusFilter')}>
                    <X size={13} />
                  </ChipClear>
                </FilterChip>
              )}
            </>
          ) : null}
        />
      ), [
        t, fetching, columns, data, pagination,
        handleNewButtonClick, filterList, handleFilterConfirm, handleDataStateChange,
        pendingOnly, clearPendingFilter,
        statusScope, statusScopeLabels, clearStatusFilter,
      ])}
    </Container>
  );
}
